import React from 'react';

import { LayoutConfig } from '@editor/types/Artboard';
import { IconComponent } from '@repo/ui/components/Icon';

export interface OptionCustomStyles {
  height?: string;
  lineHeight?: string;
  padding?: string;
}

export interface CustomStyles {
  width?: string;
  maxWidth?: string;
  maxHeight?: string;
  left?: string;
  right?: string;
  boxShadow?: string;
  border?: string;
}

export type Option = {
  id: string | number;
  default?: boolean;
  label: string;
  unselectable?: boolean;
  /**Artboard size selection option */
  icon?: React.ReactElement | IconComponent | React.ReactNode;
  size?: LayoutConfig['size'];
  /** Dropdown option category */
  category?: keyof typeof DropdownOptionCategory;
  tooltipText?: string;
};

interface CommonDropdownProps<TOption extends Option> {
  compact?: boolean;
  small?: boolean;
  optionCustomStyles?: OptionCustomStyles;
  /** Providing a render Item will overwrite built-in functionality & style, eg: checkmark on selected items  */
  renderItem?: (option: TOption, isController?: boolean) => React.ReactElement;
}

export interface DropdownProps<TOption extends Option = Option>
  extends CommonDropdownProps<TOption> {
  options: readonly TOption[];
  expanded: boolean;
  activeOption?: TOption;
  activeOptions?: TOption[];
  onOptionClick?: (option: TOption) => void;
  onMouseEnterOption?: (option: TOption) => void;
  onMouseLeaveOption?: (option: TOption) => void;
  customStyles?: CustomStyles;
  dataTestId?: string;
  onPointerDownAway?: (event: MouseEvent) => unknown;
  offsetParent?: Element | null;
  relativeParent?: Element | null;
  forceBottomDropdown?: boolean;
  searchText?: string;
  minSafeMargin?: number;
}

export interface DropdownOptionProps<TOption extends Option = Option>
  extends CommonDropdownProps<TOption> {
  option: TOption;
  isActive: boolean;
  onClick?: (option: TOption) => void;
  onMouseEnter?: (option: TOption) => void;
  onMouseLeave?: (option: TOption) => void;
}

/** Dropdown option category
 * This is to render a title of grouped options in the dropdown, or to render a divider between groups.
 * See CU-86bzdc6tq
 */
export enum DropdownOptionCategory {
  Option = 'Option',
  GroupTitle = 'GroupTitle',
  Divider = 'Divider',
}
